import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Trash2, AlertTriangle, CheckCircle2, Sparkles, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * One-shot admin modal for purging the Phase-1 "Margaret Henderson"
 * sample data out of the preview/staging DB. Two-step flow to prevent
 * accidents:
 *   1. Opens → auto-runs dry-run → shows counts or "DB is clean".
 *   2. If matches found → shows a destructive "Delete all" confirm.
 * The server-side endpoint is admin-token-guarded and idempotent; this
 * UI is just a friendly front-door so coordinators don't need curl.
 */
export default function LegacyCleanupModal({ open, onClose, onApplied }) {
  const [dryRun, setDryRun] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = () => {
    axios
      .get(`${API}/admin/actions`, { params: { action: "cleanup_legacy_margaret", limit: 5 } })
      .then((res) => setHistory(res.data?.actions || []))
      .catch(() => { /* non-blocking */ });
  };

  useEffect(() => {
    if (!open) {
      setDryRun(null);
      setApplied(null);
      setBusy(false);
      setErr("");
      setHistory([]);
      return;
    }
    setBusy(true);
    axios
      .post(`${API}/admin/cleanup-legacy-margaret`, { dry_run: true })
      .then((res) => setDryRun(res.data))
      .catch((e) => setErr(e?.response?.data?.detail || e?.message || "Could not run dry run."))
      .finally(() => setBusy(false));
    loadHistory();
  }, [open]);

  if (!open) return null;

  const apply = async () => {
    setBusy(true);
    setErr("");
    try {
      // Pull a usable actor identifier from localStorage if present —
      // populated by parentAuth on JWT exchange — so the audit row
      // records who clicked the destructive button.
      let actor = "";
      try {
        const ctx = JSON.parse(localStorage.getItem("avida_assignment") || "{}");
        actor = ctx?.assigned_rn || ctx?.rn_email || "";
      } catch { /* ignore */ }
      const res = await axios.post(
        `${API}/admin/cleanup-legacy-margaret`,
        { dry_run: false },
        { headers: actor ? { "X-Admin-Actor": actor } : {} },
      );
      setApplied(res.data);
      toast.success("Legacy data swept");
      loadHistory();
      onApplied?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Sweep failed.");
    } finally {
      setBusy(false);
    }
  };

  const isClean = dryRun?.status === "no_legacy_data_found";
  const hasMatches = dryRun?.status === "matches_found";
  const total = dryRun
    ? (dryRun.matched.pa_jobs + dryRun.matched.audit_records + dryRun.matched.intake_drafts)
    : 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0B1E3A]/40 flex items-center justify-center px-4 py-12"
      data-testid="legacy-cleanup-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">
              Admin cleanup
            </div>
            <h2 className="font-display text-xl font-bold text-[#0B1E3A] mt-1">
              Sweep legacy sample data
            </h2>
            <div className="text-[11.5px] text-[#3A5275] mt-1 max-w-[340px]">
              Removes the Phase-1 "Margaret Henderson" demo records from Mongo
              (pa_jobs, audit_records, intake_drafts + cascaded sync alerts +
              /tmp job dirs).
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[#3A5275] hover:text-[#0B1E3A] disabled:opacity-50"
            data-testid="legacy-cleanup-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {busy && !dryRun && (
          <div
            className="flex items-center gap-2 text-[12.5px] text-[#3A5275] px-3 py-4 rounded-lg bg-[#F1F5F9]"
            data-testid="legacy-cleanup-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Scanning for legacy data…
          </div>
        )}

        {err && (
          <div
            className="rounded-lg border border-[#E6B8B8] bg-[#FBECEC] px-3 py-2 text-[12.5px] text-[#A42A2A] mb-3"
            data-testid="legacy-cleanup-error"
          >
            {err}
          </div>
        )}

        {/* No matches — nothing to do */}
        {dryRun && isClean && !applied && (
          <div
            className="rounded-lg border border-[#B5D9C3] bg-[#E9F5EE] px-4 py-4"
            data-testid="legacy-cleanup-clean"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#0E6B3F] shrink-0 mt-0.5" />
              <div>
                <div className="text-[14px] font-semibold text-[#0B1E3A]">
                  Preview DB is already clean
                </div>
                <div className="text-[12px] text-[#3A5275] mt-1">
                  No legacy Margaret Henderson records found. Nothing to sweep.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Matches found — show counts + destructive confirm */}
        {dryRun && hasMatches && !applied && (
          <>
            <div
              className="rounded-lg border border-[#F0E29A] bg-[#FFFDEC] px-4 py-3"
              data-testid="legacy-cleanup-matches"
            >
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-[#8A6B00] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-[#0B1E3A]">
                    Found {total} legacy records
                  </div>
                  <ul className="text-[12px] text-[#3A5275] mt-1.5 space-y-0.5">
                    <li>
                      <span className="font-mono">pa_jobs:</span>{" "}
                      <span className="font-semibold text-[#0B1E3A]" data-testid="legacy-count-pa_jobs">
                        {dryRun.matched.pa_jobs}
                      </span>
                    </li>
                    <li>
                      <span className="font-mono">audit_records:</span>{" "}
                      <span className="font-semibold text-[#0B1E3A]" data-testid="legacy-count-audit_records">
                        {dryRun.matched.audit_records}
                      </span>
                    </li>
                    <li>
                      <span className="font-mono">intake_drafts:</span>{" "}
                      <span className="font-semibold text-[#0B1E3A]" data-testid="legacy-count-intake_drafts">
                        {dryRun.matched.intake_drafts}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-[#E6B8B8] bg-[#FBECEC] px-3 py-2 flex items-start gap-2 mt-3">
              <AlertTriangle className="w-4 h-4 text-[#A42A2A] shrink-0 mt-0.5" />
              <div className="text-[12px] text-[#A42A2A]">
                Destructive and irreversible. Use only on preview/staging DBs —
                never on production without an outage window.
              </div>
            </div>
          </>
        )}

        {/* Applied — show receipt */}
        {applied && (
          <div
            className="rounded-lg border border-[#B5D9C3] bg-[#E9F5EE] px-4 py-3"
            data-testid="legacy-cleanup-applied"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#0E6B3F] shrink-0 mt-0.5" />
              <div>
                <div className="text-[14px] font-semibold text-[#0B1E3A]">
                  {applied.status === "no_legacy_data_found" ? "Already clean" : "Swept"}
                </div>
                <ul className="text-[12px] text-[#3A5275] mt-1.5 space-y-0.5">
                  <li>pa_jobs removed: <span className="font-semibold text-[#0B1E3A]">{applied.deleted.pa_jobs}</span></li>
                  <li>audit_records removed: <span className="font-semibold text-[#0B1E3A]">{applied.deleted.audit_records}</span></li>
                  <li>intake_drafts removed: <span className="font-semibold text-[#0B1E3A]">{applied.deleted.intake_drafts}</span></li>
                  <li>draft_sync_alerts cascaded: <span className="font-semibold text-[#0B1E3A]">{applied.deleted.draft_sync_alerts}</span></li>
                  <li>job dirs wiped: <span className="font-semibold text-[#0B1E3A]">{applied.deleted.job_dirs}</span></li>
                </ul>
                {applied.action_id && (
                  <div className="text-[10.5px] text-[#3A5275] mt-2 font-mono break-all">
                    Audit ID: {applied.action_id}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* History — recent sweeps audit trail (non-blocking) */}
        {history.length > 0 && (
          <div className="mt-4" data-testid="legacy-cleanup-history">
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] font-semibold text-[#3A5275] mb-2">
              <History className="w-3.5 h-3.5" /> Recent sweeps · newest first
            </div>
            <ul className="space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
              {history.map((h, idx) => {
                const total =
                  (h.deleted?.pa_jobs || 0) +
                  (h.deleted?.audit_records || 0) +
                  (h.deleted?.intake_drafts || 0);
                return (
                  <li
                    key={h.action_id || idx}
                    className="rounded-md border border-[#D6E3EF] bg-white px-2.5 py-1.5 text-[11.5px] text-[#3A5275]"
                    data-testid={`legacy-cleanup-history-row-${idx}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono truncate">{h.actor || "unknown"}</span>
                      <span className="text-[10.5px]">{fmtTs(h.timestamp)}</span>
                    </div>
                    <div className="text-[10.5px] mt-0.5">
                      Removed{" "}
                      <span className="font-semibold text-[#0B1E3A]">{total}</span>{" "}
                      records
                      {" "}(pa_jobs {h.deleted?.pa_jobs || 0},{" "}
                      audit {h.deleted?.audit_records || 0},{" "}
                      drafts {h.deleted?.intake_drafts || 0})
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn-ghost"
            data-testid="legacy-cleanup-cancel"
          >
            {applied || isClean ? "Close" : "Cancel"}
          </button>
          {dryRun && hasMatches && !applied && (
            <button
              type="button"
              onClick={apply}
              disabled={busy}
              className="btn-primary"
              style={{ background: "#A42A2A", borderColor: "#A42A2A" }}
              data-testid="legacy-cleanup-apply"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Sweeping…</>
                : <><Trash2 className="w-4 h-4" /> Delete all {total}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
